import React, { useState } from 'react';
import { X, Calendar, Save, Lock } from 'lucide-react';

interface CreateCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (calendarData: any) => void;
}

const CreateCalendarModal: React.FC<CreateCalendarModalProps> = ({ isOpen, onClose, onSave }) => {

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    type: 'personal' as 'personal' | 'shared' | 'team',
    is_visible: true,
    timezone: 'Europe/Istanbul'
  });

  const [isSaving, setIsSaving] = useState(false);

  const predefinedColors = [
    { name: 'Mavi', value: '#3B82F6' },
    { name: 'Yeşil', value: '#10B981' },
    { name: 'Mor', value: '#8B5CF6' },
    { name: 'Kırmızı', value: '#EF4444' },
    { name: 'Sarı', value: '#F59E0B' },
    { name: 'Pembe', value: '#EC4899' },
    { name: 'Turuncu', value: '#F97316' },
    { name: 'Gri', value: '#6B7280' }
  ];



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await onSave({
        ...formData
      });

      // Reset form
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        type: 'personal',
        is_visible: true,
        timezone: 'Europe/Istanbul'
      });
    } catch (error) {
      console.error('Calendar creation error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Yeni Takvim Oluştur</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Takvim Bilgileri</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Takvim Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: İş Takvimim, Kişisel Etkinlikler"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Takvim hakkında kısa açıklama..."
                />
              </div>
            </div>
          </div>



          {/* Appearance */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Görünüm</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Renk Seçin
                </label>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-10 h-10 rounded-full border-4 transition-all ${formData.color === color.value
                        ? 'border-gray-900 dark:border-white scale-110'
                        : 'border-gray-200 dark:border-gray-700 hover:scale-105'
                        }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <input
                    type="color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className="w-16 h-8 rounded border border-gray-200 dark:border-gray-700"
                  />
                  <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                    Özel renk seçin
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Ayarlar</h3>
            <div className="space-y-4">


              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Saat Dilimi
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Europe/Istanbul">İstanbul (UTC+3)</option>
                  <option value="Europe/London">Londra (UTC+0)</option>
                  <option value="Europe/Berlin">Berlin (UTC+1)</option>
                  <option value="Europe/Rome">Roma (UTC+1)</option>
                  <option value="America/New_York">New York (UTC-5)</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  id="is_visible"
                  type="checkbox"
                  name="is_visible"
                  checked={formData.is_visible}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="is_visible" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Takvimi görünür yap
                </label>
              </div>
            </div>
          </div>

          {/* Preview */}
          {formData.name && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Önizleme</h4>
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: formData.color }}
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{formData.name}</p>
                  {formData.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formData.description}</p>
                  )}
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      Kişisel Takvim
                    </span>
                    <Lock className="w-3 h-3 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSaving || !formData.name.trim()}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSaving || !formData.name.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25'
                }`}
            >
              <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              <span>{isSaving ? 'Oluşturuluyor...' : 'Takvim Oluştur'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCalendarModal;